import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import {
  BankTransaction,
  ReconciliationSummary,
  parseBankCSV,
  autoMatchTransactions,
  calculateReconciliationSummary,
} from "@/lib/accounting/reconciliation";
import { useAccounting } from "./AccountingContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import * as XLSX from "xlsx";

interface ReconciliationContextType {
  bankTransactions: BankTransaction[];
  summary: ReconciliationSummary;
  importFromCSV: (text: string) => void;
  importFromExcel: (file: File) => Promise<void>;
  runAutoMatch: () => void;
  markReconciled: (id: string) => void;
  markUnmatched: (id: string) => void;
  unmatchTransaction: (id: string) => void;
  manualMatch: (txnId: string, voucherId: string) => void;
  clearAll: () => void;
}

const ReconciliationContext = createContext<ReconciliationContextType | null>(null);

const DEFAULT_SUMMARY: ReconciliationSummary = {
  bankBalance: 0, ledgerBalance: 0, difference: 0,
  totalTransactions: 0, matched: 0, unmatched: 0, reconciled: 0,
};

export function ReconciliationProvider({ children }: { children: ReactNode }) {
  const { vouchers, ledgers } = useAccounting();
  const { toast } = useToast();
  const [bankTransactions, setBankTransactions] = useState<BankTransaction[]>([]);

  // Get bank ledger balance
  const bankLedger = ledgers.find((l) => l.group === "Bank Accounts");
  const ledgerBalance = bankLedger?.currentBalance ?? 0;

  const summary = bankTransactions.length > 0
    ? calculateReconciliationSummary(bankTransactions, ledgerBalance)
    : { ...DEFAULT_SUMMARY, ledgerBalance };

  const importFromCSV = useCallback((text: string) => {
    const txns = parseBankCSV(text);
    if (txns.length === 0) {
      toast({ title: "Import failed", description: "No valid transactions found in CSV", variant: "destructive" });
      return;
    }
    setBankTransactions(txns);
    toast({ title: `${txns.length} transactions imported`, description: "Run auto-match to find matching entries" });
  }, [toast]);

  const importFromExcel = useCallback(async (file: File) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const csv = XLSX.utils.sheet_to_csv(sheet);
    importFromCSV(csv);
  }, [importFromCSV]);

  const runAutoMatch = useCallback(() => {
    const matched = autoMatchTransactions(bankTransactions, vouchers);
    setBankTransactions(matched);
    const newMatches = matched.filter((t) => t.status === "Matched").length;
    toast({ title: "Auto-match complete", description: `${newMatches} transaction${newMatches !== 1 ? "s" : ""} matched` });
  }, [bankTransactions, vouchers, toast]);

  const markReconciled = useCallback((id: string) => {
    setBankTransactions((prev) =>
      prev.map((t) => {
        if (t.id === id && t.status === "Matched") {
          if (t.matchedVoucherId) {
            supabase
              .from("vouchers")
              .update({
                reconciled: 1,
                reconciled_at: new Date().toISOString(),
                reconciliation_ref: t.reference || t.description,
              })
              .eq("id", t.matchedVoucherId)
              .then();
          }
          return { ...t, status: "Reconciled" as const };
        }
        return t;
      })
    );
  }, []);

  const markUnmatched = useCallback((id: string) => {
    setBankTransactions((prev) => prev.map((t) =>
      t.id === id ? { ...t, status: "Unmatched", matchedVoucherId: undefined } : t
    ));
  }, []);

  const unmatchTransaction = useCallback((id: string) => {
    setBankTransactions((prev) => prev.map((t) =>
      t.id === id ? { ...t, status: "Unmatched", matchedVoucherId: undefined } : t
    ));
  }, []);

  const manualMatch = useCallback((txnId: string, voucherId: string) => {
    setBankTransactions((prev) => prev.map((t) =>
      t.id === txnId ? { ...t, matchedVoucherId: voucherId, status: "Matched" } : t
    ));
  }, []);

  const clearAll = useCallback(() => {
    setBankTransactions([]);
    toast({ title: "Cleared", description: "All bank transactions removed" });
  }, [toast]);

  return (
    <ReconciliationContext.Provider value={{
      bankTransactions, summary,
      importFromCSV, importFromExcel, runAutoMatch,
      markReconciled, markUnmatched, unmatchTransaction, manualMatch, clearAll,
    }}>
      {children}
    </ReconciliationContext.Provider>
  );
}

export function useReconciliation() {
  const ctx = useContext(ReconciliationContext);
  if (!ctx) throw new Error("useReconciliation must be used within ReconciliationProvider");
  return ctx;
}
